// src/pages/sessions/components/LiveSession/mobile/PhoneVideoArea.jsx
import React from 'react';
import VideoContainer from '../VideoContainer';
import { WhiteboardCanvas, WhiteboardBottomBar } from '../whiteboard';

/**
 * PhoneVideoArea
 *
 * Renders the video grid (or whiteboard) for the phone layout.
 *
 * Layout modes:
 *  - Normal: VideoContainer fills the area with layoutMode='phone'
 *  - Screen share active: VideoContainer handles it via layoutMode='phone'
 *    (VideoGrid detects screenSharers and switches to presentation mode)
 *  - Whiteboard active: WhiteboardCanvas fills the area, VideoContainer
 *    shrinks to a mini strip at top (future polish — for now whiteboard
 *    takes the full area when active)
 *
 * The tap handler on the video area shows/hides the controls overlay.
 */
const PhoneVideoArea = ({
  // VideoContainer props (passed through directly)
  screenSharers,
  embeddedDocument,
  documentSharer,
  localParticipantIdentity,
  virtualRooms,
  participantVirtualRooms,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms,
  remoteTeacherStates,
  currentVirtualRoom,
  isTeacher,
  hideOwnVideo,
  gracePeriodParticipants,
  // Whiteboard
  isWhiteboardActive,
  isWhiteboardDetached,
  whiteboardManager,
}) => {

  // Build whiteboard component for VideoContainer (same as desktop pattern)
  const whiteboardComponent = (isWhiteboardActive && whiteboardManager) ? (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-hidden">
        <WhiteboardCanvas
          fabricRef={whiteboardManager.fabricCanvasRef}
          activeTool={whiteboardManager.activeTool}
          activeColor={whiteboardManager.activeColor}
          brushSize={whiteboardManager.brushSize}
          // Students on phone: read-only. Teacher on phone: can draw.
          canDraw={whiteboardManager.canDraw}
          isTeacher={isTeacher}
          onPathCreated={whiteboardManager.handlePathCreated}
          onObjectModified={whiteboardManager.handleObjectModified}
          onSelectionChange={whiteboardManager.handleSelectionChange}
          onStrokeStart={whiteboardManager.handleStrokeStart}
          onStrokePoint={whiteboardManager.handleStrokePoint}
          onStrokeEnd={whiteboardManager.handleStrokeEnd}
          onShapeCreated={whiteboardManager.handleShapeCreated}
          onTextCreated={whiteboardManager.handleTextCreated}
          onObjectErased={whiteboardManager.handleObjectErased}
          onToolChange={whiteboardManager.setActiveTool}
          onDeleteSelected={whiteboardManager.deleteSelected}
          onAddImage={whiteboardManager.handleAddImage}
        />
      </div>
      {/* Compact whiteboard toolbar for phone — only basic tools */}
      <WhiteboardBottomBar
        activeTool={whiteboardManager.activeTool}
        toolSettings={whiteboardManager.toolSettings}
        isTeacher={isTeacher}
        canDraw={whiteboardManager.canDraw}
        studentDrawingEnabled={whiteboardManager.studentDrawingEnabled}
        onToolChange={whiteboardManager.setActiveTool}
        onToolSettingsChange={whiteboardManager.updateToolSettings}
        onClear={whiteboardManager.clearCanvas}
        onClose={whiteboardManager.closeWhiteboard}
        onToggleStudentDrawing={whiteboardManager.toggleStudentDrawing}
        isDetached={false}
        onDetach={null}
        onAttach={null}
      />
    </div>
  ) : null;

  return (
    <div
      className="flex-1 relative min-h-0 overflow-hidden"
    >
      <VideoContainer
        layoutMode="phone"
        screenSharers={screenSharers}
        embeddedDocument={embeddedDocument}
        documentSharer={documentSharer}
        localParticipantIdentity={localParticipantIdentity}
        virtualRooms={virtualRooms}
        participantVirtualRooms={participantVirtualRooms}
        teacherBroadcastRooms={teacherBroadcastRooms}
        teacherMonitorRooms={teacherMonitorRooms}
        teacherMonitorSilentRooms={teacherMonitorSilentRooms}
        remoteTeacherStates={remoteTeacherStates}
        currentVirtualRoom={currentVirtualRoom}
        isTeacher={isTeacher}
        hideOwnVideo={hideOwnVideo}
        gracePeriodParticipants={gracePeriodParticipants}
        isWhiteboardActive={isWhiteboardActive}
        isWhiteboardDetached={isWhiteboardDetached}
        whiteboardComponent={whiteboardComponent}
      />
    </div>
  );
};

export default PhoneVideoArea;
